import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, Mail, Lock, Eye, EyeOff, User } from 'lucide-react';
import logo from '../assets/logo.png';
import testFirebaseConnection from '../testFirebase';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    if (password.length < 6) {
      return setError('Password should be at least 6 characters');
    }

    setLoading(true);

    try {
      const result = await signup(email, password);
      console.log('Signup successful:', result);
      navigate('/dashboard');
    } catch (err) {
      console.error('Signup error:', err);
      let errorMessage = 'Failed to create an account. Please try again.';
      
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please use a different email or sign in.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address. Please check and try again.';
      } else if (err.code === 'auth/configuration-not-found') {
        errorMessage = 'Authentication is not configured. Please enable Email/Password authentication in Firebase.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Premium Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-olive-50 via-emerald-50 to-green-50"></div>
      
      {/* Premium Textures */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-96 h-96 bg-olive-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-200 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-200 rounded-full blur-3xl"></div>
      </div>
      
      {/* Premium Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="h-full w-full" style={{
          backgroundImage: `linear-gradient(rgba(22, 101, 52, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(22, 101, 52, 0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Premium Card */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-olive-600/20 to-emerald-600/20 rounded-3xl blur-2xl"></div>
            
            {/* Main Card */}
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
              {/* Card Overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-3xl"></div>
              
              {/* Content */}
              <div className="relative p-8">
                {/* Logo Section */}
                <div className="text-center mb-8">
                  <div className="relative inline-block mb-6">
                    {/* Logo Glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-olive-600 to-emerald-600 rounded-2xl blur-xl opacity-50 scale-110"></div>
                    
                    {/* Logo Container */}
                    <div className="relative w-20 h-20 bg-gradient-to-br from-olive-600 via-emerald-600 to-green-600 rounded-2xl flex items-center justify-center shadow-2xl border border-white/10">
                      <img src={logo} alt="TravelSplit" className="w-16 h-16 object-contain" />
                    </div>
                  </div>
                  
                  <h1 className="text-3xl font-bold mb-2">
                    <span className="font-black tracking-wide" style={{ fontFamily: 'Playfair Display, Georgia, serif', letterSpacing: '0.05em' }}>
                      <span className="text-black">Travel</span>
                      <span className="text-black">v</span>
                      <span className="text-black">el</span>
                      <span style={{ color: '#166534' }}>Split</span>
                    </span>
                  </h1>
                  <p className="text-gray-700 mt-2">Create your account to start splitting expenses!</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-6 p-4 bg-red-600/20 backdrop-blur border border-red-600/30 rounded-xl flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-300 mr-3 flex-shrink-0" />
                    <span className="text-red-100 text-sm">{error}</span>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-5 h-5 text-olive-600" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-olive-500 text-gray-800 placeholder-gray-600"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-5 h-5 text-olive-600" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-olive-500 text-gray-800 placeholder-gray-600"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-5 h-5 text-olive-600" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-olive-500 text-gray-800 placeholder-gray-600"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-olive-600 hover:text-olive-700"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-5 h-5 text-olive-600" />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 bg-white/20 backdrop-blur border border-white/30 rounded-xl focus:ring-2 focus:ring-olive-500/50 focus:border-olive-500 text-gray-800 placeholder-gray-600"
                        placeholder="Confirm your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-3 text-olive-600 hover:text-olive-700"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-olive-600 via-emerald-600 to-green-600 text-white py-3 rounded-xl font-bold hover:from-olive-700 hover:via-emerald-700 hover:to-green-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </form>

                {/* Sign In Link */}
                <div className="mt-8 text-center">
                  <p className="text-gray-700 text-sm">
                    Already have an account?{' '}
                    <Link
                      to="/login"
                      className="font-medium text-gray-800 hover:text-gray-900 transition-colors duration-200 underline underline-offset-2 decoration-2 decoration-gray-400/50 hover:decoration-gray-300"
                    >
                      Sign in here
                    </Link>
                  </p>
                </div>

                {/* Premium Badge */}
                <div className="mt-6 flex items-center justify-center">
                  <div className="flex items-center space-x-2 px-3 py-1 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur border border-amber-400/30 rounded-full">
                    <span className="text-amber-300 text-xs"></span>
                    <span className="text-black text-xs font-medium">Created By @HarshitAggarwal</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
